import {
	DeleteOutlined,
	EditOutlined,
	EyeOutlined,
	PrinterOutlined,
	RollbackOutlined,
	SyncOutlined,
	TagOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Space, Table, Tag, Tooltip, Input } from "antd";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import db from "../../db/database";
import {
	getAllClients,
	getClientById,
	getClientByNo,
} from "../../services/clientService";
import { getColegioByNo } from "../../services/colegioService";
import { getInvoicePaymentLines } from "../../services/invoicePaymentService";
import {
	deleteInvoice,
	getAllInvoices,
	getCompleteInvoice,
	getTodaysInvoicesByType,
	getPendingSyncInvoices,
} from "../../services/invoiceService";
import { getFinalDocumentNo, syncInvoiceToBC } from "../../services/invoiceSyncService";
import {
	findInvoiceInBCByIdTpv,
	reconcileInvoiceWithBC,
	releaseStuckInvoice,
} from "../../services/invoiceRecoveryService";
import { getTPVByNoOrId } from "../../services/tpvService";
import "./InvoiceStyles.css";
import { getAllTurnos } from "../../services/turnoService";
import dayjs from "dayjs";
import { calculateInvoiceTotalsHelper } from "../../utils/invoiceTotals";
import { getInvoiceListConfig } from "./configs/invoiceList.config";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { useCountryExtension } from "./extensions/useCountryExtension";

const InvoiceList = ({
	onEdit,
	processInvoiceForCreditMemo,
	documentType = "Invoice", // Nueva prop para filtrar por tipo de documento ("Invoice" o "Credit Memo")
	syncStatus, // Nueva prop para filtrar por estado de sincronización ("pending")
	dateFilter, // Nueva prop para filtrar por fecha ("today")
}) => {
	const { country } = useConfig();
	const { showNotasCredito, showTicketGenerico, showTicket, showFactura, showContabilizar } = getInvoiceListConfig(country?.code);
	const [invoices, setInvoices] = useState([]);
	const [filteredInvoices, setFilteredInvoices] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
	});
	// Estado para controlar el loading por factura individual
	const [selectedInvoice, setSelectedInvoice] = useState(null);
	const [colegio, setColegio] = useState(null);
	const [paymentLines, setPaymentLines] = useState([]);
	const [detailVisible, setDetailVisible] = useState(false);
	const [ticketModalVisible, setTicketModalVisible] = useState(false);
	const [selectedInvoiceForTicket, setSelectedInvoiceForTicket] =
		useState(null);
	const [selectedClientForTicket, setSelectedClientForTicket] = useState(null);
	const [selectedTPVForTicket, setSelectedTPVForTicket] = useState(null);
	const [clients, setClients] = useState([]);
	const [turnos, setTurnos] = useState(null);

	// Inicializo extension por pais. Obtenemos los componentes "slot"
	const {
		DownloadTicketButtonSlot,
		DownloadInvoiceButtonSlot,
		PostInvoiceButtonSlot,
		TicketSectionSlot
	} = useCountryExtension();

	const loadingRef = useRef(false);
	console.log(selectedInvoice)
	const loadInvoices = useCallback(async () => {
		try {
			if (loadingRef.current) return;
			loadingRef.current = true;

			setLoading(true);

			let result = [];
			// Calcular los modos dentro del callback para evitar dependencias
			const isPending = syncStatus === "pending";
			const isTodayFilter = dateFilter === "today";

			// Si es filtro de hoy, usar las funciones específicas optimizadas
			if (isPending) {
				result = await getPendingSyncInvoices({ dateFilter });
			} else if (isTodayFilter) {
				result = await getTodaysInvoicesByType(documentType);
			} else {
				const all = await getAllInvoices();
				result = all.filter((i) => i.Document_Type === documentType);
			}

			setInvoices(result);
		}
		catch (error) {
			console.error("Error al cargar facturas:", error);
			const isPending = syncStatus === "pending";
			const isInvoice = documentType === "Invoice";
			const errorMessage = isPending
				? "las facturas pendientes de sincronizar"
				: isInvoice
					? "las facturas"
					: "los abonos";
			window.messageApi.error(`No se pudieron cargar ${errorMessage}`);
		}
		finally {
			loadingRef.current = false;
			setLoading(false);
		}
	}, [documentType, syncStatus, dateFilter]);

	// Helpers estado factura
	const isInvoiceSynced = (invoice) => {
		return (!invoice.syncError &&
			(invoice.syncStage === "payments" || invoice.syncStage === "posted"));
	};

	const isInvoiceError = (invoice) => {
		return !!invoice.syncError;
	};

	const isInvoicePartialSync = (invoice) => {
		return (
			!invoice.syncError &&
			invoice.pendingSync &&
			!isInvoiceSynced(invoice)
		);
	};


	// Cargar clientes al montar el componente
	useEffect(() => {
		const loadClients = async () => {
			try {
				const clientList = await getAllClients();
				setClients(clientList);
			} catch (error) {
				console.error("Error al cargar clientes:", error);
			}
		};
		loadClients();
	}, []);

	useEffect(() => {
		const loadTurnos = async () => {
			const turnos = await getAllTurnos();
			setTurnos(turnos);
		};
		loadTurnos();
	}, []);

	useEffect(() => {
		const onCreate = () => loadInvoices();
		const onDelete = () => loadInvoices();

		const onUpdate = (mods, id) => {
			const hasNo = "No" in mods;
			const hasPreAssignedNo = "Pre_Assigned_No" in mods;
			const hasPosted = "posted" in mods;
			const hasPendingSync = "pendingSync" in mods;
			const hasLastSynced = "lastSynced" in mods;
			const hasSystemId = "systemId" in mods;
			const hasNumeroComprobante = "No_Comprobante_Fiscal" in mods;
			const hasSyncing = "syncing" in mods;
			const hasSyncStage = "syncStage" in mods;
			const hasSyncError = "syncError" in mods;
			const hasSyncErrorMessage = "syncErrorMessage" in mods;

			//Para actualizar SOLO los campos que realmente cambiaron
			if (hasNo || hasPreAssignedNo || hasPosted || hasPendingSync || hasLastSynced || hasNumeroComprobante || hasSyncing || hasSyncStage || hasSyncError || hasSyncErrorMessage) {
				setInvoices(prev =>
					prev.map(inv =>
						inv.id === id
							? {
								...inv,
								...(hasNo && { No: mods.No }),
								...(hasPreAssignedNo && { Pre_Assigned_No: mods.Pre_Assigned_No }),
								...(hasPosted && { posted: mods.posted }),
								...(hasPendingSync && { pendingSync: mods.pendingSync }),
								...(hasLastSynced && { lastSynced: mods.lastSynced }),
								...(hasSystemId && { systemId: mods.systemId }),
								...(hasNumeroComprobante && { No_Comprobante_Fiscal: mods.No_Comprobante_Fiscal }),
								...(hasSyncing && { syncing: mods.syncing }),
								...(hasSyncStage && { syncStage: mods.syncStage }),
								...(hasSyncError && { syncError: mods.syncError }),
								...(hasSyncErrorMessage && { syncErrorMessage: mods.syncErrorMessage })
							}
							: inv
					)
				);
			}
		};

		db.invoices.hook("creating", onCreate);
		db.invoices.hook("deleting", onDelete);
		db.invoices.hook("updating", onUpdate);

		return () => {
			db.invoices.hook("creating").unsubscribe(onCreate);
			db.invoices.hook("deleting").unsubscribe(onDelete);
			db.invoices.hook("updating").unsubscribe(onUpdate);
		};
	}, [loadInvoices]);

	// Determinar si estamos en modo facturas o abonos
	const isInvoiceMode = documentType === "Invoice";
	const isPendingMode = syncStatus === "pending";

	// Función para limpiar facturas que llevan mucho tiempo sincronizándose
	const cleanupStuckSyncingInvoices = async () => {
		try {
			const ninetySecondsAgo = new Date(
				Date.now() - 90 * 1000
			).toISOString();

			/**
			 * Buscar facturas trabadas
			 */
			const stuckInvoices = await db.invoices
				.filter(
					(invoice) =>
						(invoice.syncing === true ||
							invoice.syncing === 1) &&
						invoice.syncStartTime &&
						invoice.syncStartTime < ninetySecondsAgo &&
						invoice.pendingSync
				)
				.toArray();

			if (!stuckInvoices.length) {
				return;
			}

			console.warn(
				`Facturas trabadas encontradas: ${stuckInvoices.length}`
			);

			for (const invoice of stuckInvoices) {
				try {
					console.warn(
						`Chequeando factura trabada ${invoice.id}`
					);

					//Si tiene Id_TPV verificar primero en BC
					if (invoice.Id_TPV) {
						const bcInvoice = await findInvoiceInBCByIdTpv(invoice.Id_TPV);

						//Existe en BC reconciliar
						if (bcInvoice) {
							console.warn(
								`Factura ${invoice.id} encontrada en BC`
							);

							await reconcileInvoiceWithBC(invoice, bcInvoice);
						}
						else {
							//NO existe en BC liberar lock
							console.warn(
								`Liberando lock factura ${invoice.id}`
							);

							await releaseStuckInvoice(invoice.id);
						}
					}
				} catch (error) {
					console.error(
						`Error procesando factura trabada ${invoice.id}`,
						error
					);
				}
			}
		} catch (error) {
			console.error(
				"Error limpiando facturas trabadas:",
				error
			);
		}
	};

	useEffect(() => {
		loadInvoices();
	}, [loadInvoices]);

	useEffect(() => {
		cleanupStuckSyncingInvoices();
	}, []);

	// Función para filtrar facturas por texto de búsqueda
	const filterInvoicesBySearch = useCallback(
		(invoiceList, searchValue) => {
			if (!searchValue) return invoiceList;

			const searchLower = searchValue.toLowerCase();
			return invoiceList.filter((invoice) => {
				// Obtener información del cliente
				const clientInfo = clients.find(
					(client) =>
						client.No === invoice.Sell_to_Customer_No_ ||
						client.id === invoice.clientLocalId
				);

				// Obtener información del turno
				const turnoInfo = turnos?.find((t) => t.No === invoice.turno);

				// Campos a buscar
				const searchFields = [
					invoice.id?.toString(),
					invoice.No,
					invoice.No_Comprobante_Fiscal,
					invoice.Document_Type === "Invoice"
						? "factura"
						: invoice.Document_Type === "Credit Memo"
							? "nota de crédito"
							: invoice.Document_Type,
					invoice.Sell_to_Customer_No_,
					clientInfo?.No,
					invoice.Bill_to_Name,
					invoice.Posting_Date,
					invoice.caja,
					invoice.turno,
					turnoInfo?.DescripcionTurno,
					invoice.Cupon,
					invoice.Tipo_Detalle,
					invoice.No_Detalle,
					invoice.Campaign_No_,
					invoice.isDraft ? "borrador" : "",
					isInvoiceSynced(invoice)
						? "sincronizada"
						: isInvoiceError(invoice)
							? "error"
							: invoice.pendingSync
								? "pendiente"
								: "sin sincronizar",
					invoice.deleted ? "eliminada" : "",
				];

				return searchFields.some(
					(field) =>
						field && field.toString().toLowerCase().includes(searchLower)
				);
			});
		},
		[clients, turnos]
	);

	// Efecto para filtrar facturas cuando cambia el texto de búsqueda
	useEffect(() => {
		const filtered = filterInvoicesBySearch(invoices, searchText);
		setFilteredInvoices(filtered);
	}, [invoices, searchText, filterInvoicesBySearch]);

	const handleEdit = (invoice) => {
		if (!invoice.isDraft) {
			window.messageApi.warning(
				"Solo se pueden editar facturas en estado borrador"
			);
			return;
		}
		onEdit(invoice);
	};

	const handleDelete = async (invoice) => {
		const canDeleteInvoice =
			invoice.isDraft ||
			isInvoiceError(invoice) ||
			(
				invoice.pendingSync &&
				!isInvoiceSynced(invoice)
			);

		if (isInvoiceSynced(invoice) || !canDeleteInvoice) {
			window.messageApi.warning(
				"Solo se pueden eliminar facturas en estado borrador"
			);
			return;
		}

		window.messageApi.confirm({
			title: "¿Está seguro de eliminar esta factura?",
			content: "Esta acción no se puede deshacer",
			okText: "Eliminar",
			okType: "danger",
			cancelText: "Cancelar",

			onOk: async () => {
				try {
					setLoading(true);

					if (!invoice.Id_TPV) {
						await deleteInvoice(invoice.id);

						window.messageApi.success(
							"Factura eliminada correctamente"
						);

						await loadInvoices();
						return;
					}

					const bcInvoice = await findInvoiceInBCByIdTpv(invoice.Id_TPV);

					if (!bcInvoice) {
						await deleteInvoice(invoice.id);

						window.messageApi.success(
							"Factura eliminada correctamente"
						);

						await loadInvoices();
					} else {
						await reconcileInvoiceWithBC(invoice, bcInvoice);

						window.messageApi.warning(
							`No se puede eliminar. La factura ya existe en BC con el número ${bcInvoice.No || bcInvoice.Pre_Assigned_No}`,
							{ modal: true }
						);
					}

				} catch (error) {
					console.error("Error al eliminar factura:", error);

					window.messageApi.error(
						error.message || "No se pudo eliminar la factura"
					);

				} finally {
					setLoading(false);
				}
			},
		});
	};

	const handleSync = async (invoice) => {
		// Verificar si ya se está sincronizando esta factura
		if (invoice.syncing) {
			window.messageApi.warning("Esta factura ya se está sincronizando");
			return;
		}

		// Verificar estado del borrador
		if (invoice.isDraft) {
			window.messageApi.warning(
				"No se pueden sincronizar facturas en estado borrador"
			);
			return;
		}

		// Verificar si ya está sincronizada
		if (isInvoiceSynced(invoice)) {
			window.messageApi.warning("Esta factura ya está sincronizada");
			return;
		}

		// Verificar si está eliminada
		if (invoice.deleted) {
			window.messageApi.warning("Esta factura ha sido eliminada");
			return;
		}

		try {
			setLoading(true);
			// Verificar estado actualizado de la factura desde la BD y marcar como sincronizando
			const { getInvoiceById } = await import("../../services/invoiceService");
			const currentInvoice = await getInvoiceById(invoice.id);

			// Verificar nuevamente el estado actual
			if (!currentInvoice) {
				window.messageApi.error("La factura no existe");
				return;
			}

			if (currentInvoice.isDraft) {
				window.messageApi.warning("La factura está en estado borrador");
				return;
			}

			if (isInvoiceSynced(currentInvoice)) {
				window.messageApi.warning("La factura ya está sincronizada");
				return;
			}

			if (currentInvoice.deleted) {
				window.messageApi.warning("La factura ha sido eliminada");
				return;
			}

			// Verificar si ya está marcada como sincronizando en la BD
			if (currentInvoice.syncing) {
				window.messageApi.warning("Esta factura ya se está sincronizando");
				return;
			}

			// Marcar como sincronizando en la BD INMEDIATAMENTE
			await db.invoices.update(invoice.id, {
				syncing: true,
				syncStartTime: new Date().toISOString()
			});

			window.messageApi.info("Sincronizando factura...");

			const result = await syncInvoiceToBC(currentInvoice, country);

			if (result.success) {
				window.messageApi.success("Factura sincronizada correctamente");
			} else {
				window.messageApi.error(`Error al sincronizar: ${result.message}`, { modal: true });
			}
		} catch (error) {
			console.error("Error al sincronizar factura:", error);
			window.messageApi.error("No se pudo sincronizar la factura");
		} finally {
			// Limpiar marca de sincronizando
			await db.invoices.update(invoice.id, {
				syncing: false,
				syncStartTime: null
			});
			setLoading(false);
		}
	};

	const handleCreateCreditMemo = async (invoice) => {

		const No = !invoice.No ? await handleUpdateInvoiceNo(invoice) : invoice.No

		if (invoice.isDraft) {
			window.messageApi.warning(
				"No se pueden crear notas de crédito sobre facturas en estado borrador"
			);
			return;
		}

		if (!No) {
			window.messageApi.warning(
				"No se pueden crear notas de crédito sobre facturas no contabilizadas"
			);
			return;
		}

		// Llamar a la función processInvoiceNo del componente padre
		processInvoiceForCreditMemo(invoice.No);
	};

	//Chequeo si la factura se contabilizo
	const handleUpdateInvoiceNo = async (invoice) => {
		try {
			return await getFinalDocumentNo(invoice, country);
		}
		catch (error) {
			window.messageApi.destroy(); //Elimino mensaje inmediatamente
			return null;
		}
	}

	const handleViewDetail = async (invoice) => {
		try {
			setLoading(true);
			await handleUpdateInvoiceNo(invoice);
			const completeInvoice = await getCompleteInvoice(invoice.id);
			setSelectedInvoice(completeInvoice);

			// Cargar líneas de pago
			const paymentLinesList = await getInvoicePaymentLines(invoice);
			setPaymentLines(paymentLinesList);

			// Cargar información del colegio si existe
			if (completeInvoice.Colegio) {
				try {
					const colegioData = await getColegioByNo(completeInvoice.Colegio);
					setColegio(colegioData);
				} catch (error) {
					console.error("Error al cargar información del colegio:", error);
					setColegio(null);
				}
			} else {
				setColegio(null);
			}

			setDetailVisible(true);
		} catch (error) {
			console.error("Error al obtener detalle de factura:", error);
			window.messageApi.error("No se pudo obtener el detalle de la factura");
		} finally {
			setLoading(false);
		}
	};

	const handlePrintTicket = async (invoice) => {
		try {
			setLoading(true);
			await handleUpdateInvoiceNo(invoice);
			const completeInvoice = await getCompleteInvoice(invoice.id);

			const paymentLines = await getInvoicePaymentLines(invoice);

			// Obtener información del cliente
			let clientInfo = null;
			try {
				// Intentar obtener por ID local primero (si existe clientLocalId)
				if (completeInvoice.clientLocalId) {
					clientInfo = await getClientById(completeInvoice.clientLocalId);
				}
				// Si no se encontró por ID local, intentar por No de cliente
				else if (completeInvoice.Sell_to_Customer_No_) {
					clientInfo = await getClientByNo(
						completeInvoice.Sell_to_Customer_No_
					);
				}
			} catch (error) {
				console.warn("No se pudo obtener información del cliente:", error);
			}

			// Obtener información del TPV basado en la caja de la factura
			let tpvInfo = null;
			try {
				if (completeInvoice.caja) {
					// Necesitamos obtener el TPV de la caja
					// Primero obtenemos todas las cajas para encontrar el TPV
					const { getAllCajas } = await import("../../services/cajaService");
					const cajas = await getAllCajas();
					const cajaInfo = cajas.find(c => c.No === completeInvoice.caja);

					if (cajaInfo && cajaInfo.TPV) {
						tpvInfo = await getTPVByNoOrId(cajaInfo.TPV);
					}
				}
			} catch (error) {
				console.warn("No se pudo obtener información del TPV:", error);
			}

			// Incluir las líneas de pago, cliente y TPV en el ticket
			setSelectedInvoiceForTicket({
				...completeInvoice,
				paymentLines: paymentLines,
			});
			setSelectedClientForTicket(clientInfo);
			setSelectedTPVForTicket(tpvInfo);
			setTicketModalVisible(true);
		} catch (error) {
			console.error("Error al preparar ticket para impresión:", error);
			window.messageApi.error("No se pudo preparar el ticket para impresión");
		} finally {
			setLoading(false);
		}
	};

	const getSyncStatusTag = (invoice) => {
		if (invoice.deleted) {
			return <Tag color="red">Eliminada</Tag>;
		}
		if (isInvoiceError(invoice)) {
			return (
				<Tooltip title={invoice.syncErrorMessage || "Error desconocido de sincronización"} >
					<Tag color="red">Error</Tag>
				</Tooltip>);
		}
		if (invoice.isDraft) {
			return <Tag color="orange">BORRADOR</Tag>;
		}
		if (isInvoicePartialSync(invoice)) {
			return <Tag color="orange">Pendiente</Tag>;
		}

		if (isInvoiceSynced(invoice)) {
			return <Tag color="green">Sincronizada</Tag>;
		}

		return <Tag color="default">Sin sincronizar</Tag>;
	};

	const getInvoiceTypeTag = (type) => {
		if (type === "Invoice") {
			return <Tag color="blue">Factura</Tag>;
		}
		if (type === "Credit Memo") {
			return <Tag color="purple">Nota de crédito</Tag>;
		}
		return <Tag color="default">{type}</Tag>;
	};

	// Función para obtener filtros únicos de una columna
	const getUniqueFilters = (dataIndex, render) => {
		const uniqueValues = new Set();
		filteredInvoices.forEach((invoice) => {
			let value = invoice[dataIndex];
			if (render) {
				value = render(value, invoice);
			}
			if (
				value !== undefined &&
				value !== null &&
				value !== "" &&
				value !== "-"
			) {
				uniqueValues.add(value);
			}
		});
		return Array.from(uniqueValues).map((value) => ({
			text: value,
			value: value,
		}));
	};

	// Función para obtener filtros de clientes únicos
	const getClientFilters = () => {
		const uniqueClients = new Set();
		filteredInvoices.forEach((invoice) => {
			if (
				invoice.Sell_to_Customer_No_ &&
				invoice.Sell_to_Customer_No_ !== "null"
			) {
				const client = clients.find(
					(client) =>
						client.No === invoice.Sell_to_Customer_No_ ||
						client.id === invoice.clientLocalId
				);
				if (client) {
					uniqueClients.add(client.No);
				} else {
					uniqueClients.add(invoice.Sell_to_Customer_No_);
				}
			}
		});
		return Array.from(uniqueClients).map((clientNo) => ({
			text: clientNo,
			value: clientNo,
		}));
	};

	// Función para obtener filtros de turnos únicos
	const getTurnoFilters = () => {
		const uniqueTurnos = new Set();
		filteredInvoices.forEach((invoice) => {
			if (invoice.turno && turnos) {
				const turnoData = turnos.find((t) => t.No === invoice.turno);
				if (turnoData) {
					uniqueTurnos.add(turnoData.DescripcionTurno);
				}
			}
		});
		return Array.from(uniqueTurnos).map((turnoDesc) => ({
			text: turnoDesc,
			value: turnoDesc,
		}));
	};

	// Función para obtener filtros de estado de sincronización
	const getSyncStatusFilters = () => {
		return [
			{ text: "BORRADOR", value: "draft" },
			{ text: "Sincronizada", value: "synced" },
			{ text: "Pendiente", value: "pending" },
			{ text: "Eliminada", value: "deleted" },
			{ text: "Error", value: "error" },
		];
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			hidden: true,
		},
		{
			title: "Nº",
			dataIndex: "No",
			key: "No",
			render: (text) => text || "Pendiente",
			filters: getUniqueFilters("No", (text) => text || "Pendiente"),
			filterSearch: true,
			onFilter: (value, record) => {
				const displayValue = record.No || "Pendiente";
				return displayValue === value;
			},
			sorter: (a, b) => (a.No || "").localeCompare(b.No || ""),
		},
		{
			title: "N° Comprobante",
			dataIndex: "No_Comprobante_Fiscal",
			key: "No_Comprobante_Fiscal",
			render: (text) => text || "-",
			filters: getUniqueFilters("No_Comprobante_Fiscal"),
			filterSearch: true,
			onFilter: (value, record) => record.No_Comprobante_Fiscal === value,
			sorter: (a, b) => (a.No_Comprobante_Fiscal || "").localeCompare(b.No_Comprobante_Fiscal || ""),
		},
		{
			title: "Nº preasignado",
			dataIndex: "Pre_Assigned_No",
			key: "Pre_Assigned_No",
			render: (text) => text || "Pendiente",
			filters: getUniqueFilters("Pre_Assigned_No", (text) => text || "Pendiente"),
			filterSearch: true,
			onFilter: (value, record) => record.Pre_Assigned_No === value,
			sorter: (a, b) => (a.Pre_Assigned_No || "").localeCompare(b.Pre_Assigned_No || ""),
		},
		{
			title: "Tipo",
			dataIndex: "Document_Type",
			key: "Document_Type",
			render: (text) => {
				return text === "Invoice"
					? "Factura"
					: text === "Credit Memo"
						? "Nota de crédito"
						: text;
			},
		},
		{
			title: "Cliente",
			dataIndex: "Sell_to_Customer_No_",
			key: "Sell_to_Customer_No_",
			render: (text, record) => {
				try {
					const client = clients.find(
						(c) =>
							c.No === text ||
							c.No === record.clientLocalId ||
							c.id === record.clientLocalId
					);

					if (client?.No) return client.No;
					return text || "-";
				} catch (error) {
					return text;
				}
			},
			filters: getClientFilters(),
			filterSearch: true,
			onFilter: (value, record) => {
				const client = clients.find(
					(client) =>
						client.No === record.Sell_to_Customer_No_ ||
						client.id === record.clientLocalId
				);
				return client
					? client.No === value
					: record.Sell_to_Customer_No_ === value;
			},
			sorter: (a, b) =>
				(a.Sell_to_Customer_No_ || "").localeCompare(
					b.Sell_to_Customer_No_ || ""
				),
		},
		{
			title: "Nombre",
			dataIndex: "Bill_to_Name",
			key: "Bill_to_Name",
			filters: getUniqueFilters("Bill_to_Name"),
			filterSearch: true,
			onFilter: (value, record) => record.Bill_to_Name === value,
			sorter: (a, b) =>
				(a.Bill_to_Name || "").localeCompare(b.Bill_to_Name || ""),
		},
		{
			title: "Fecha",
			dataIndex: "Posting_Date",
			key: "Posting_Date",
			render: (date) => {
				if (!date) return "-";
				return dayjs(date).format("DD/MM/YYYY");
			},
			filters: getUniqueFilters("Posting_Date", (date) => {
				if (!date) return "-";
				return dayjs(date).format("DD/MM/YYYY");
			}),
			filterSearch: true,
			onFilter: (value, record) => {
				const displayDate = record.Posting_Date
					? dayjs(record.Posting_Date).format("DD/MM/YYYY")
					: "-";
				return displayDate === value;
			},
			sorter: (a, b) =>
				new Date(a.Posting_Date || 0) - new Date(b.Posting_Date || 0),
		},
		{
			title: "Caja",
			dataIndex: "caja",
			key: "caja",
			render: (caja) => caja || "-",
			filters: getUniqueFilters("caja", (caja) => caja || "-"),
			filterSearch: true,
			onFilter: (value, record) => (record.caja || "-") === value,
			sorter: (a, b) => (a.caja || "").localeCompare(b.caja || ""),
		},
		{
			title: "Turno",
			dataIndex: "turno",
			key: "turno",
			render: (turno) => {
				const turnoData = turnos?.find((t) => t.No === turno);
				return turnoData ? turnoData.DescripcionTurno : "-";
			},
			filters: getTurnoFilters(),
			filterSearch: true,
			onFilter: (value, record) => {
				const turnoData = turnos?.find((t) => t.No === record.turno);
				return turnoData ? turnoData.DescripcionTurno === value : false;
			},
			sorter: (a, b) => {
				const turnoA = turnos?.find((t) => t.No === a.turno);
				const turnoB = turnos?.find((t) => t.No === b.turno);
				return (turnoA?.DescripcionTurno || "").localeCompare(
					turnoB?.DescripcionTurno || ""
				);
			},
		},
		{
			title: "Cupón",
			dataIndex: "cupon",
			key: "cupon",
			render: (_, record) => {
				if (record.Cupon && record.Tipo_Detalle && record.No_Detalle) {
					return (
						<Tag color="green" icon={<TagOutlined />}>
							{record.Cupon} ({record.Tipo_Detalle}: {record.No_Detalle})
						</Tag>
					);
				}
				return "-";
			},
			filters: getUniqueFilters("Cupon"),
			filterSearch: true,
			onFilter: (value, record) => record.Cupon === value,
		},
		{
			title: "Campaña",
			dataIndex: "Campaign_No_",
			key: "Campaign_No_",
			render: (campaignNo) => {
				if (campaignNo) {
					return (
						<Tag color="blue" icon={<TagOutlined />}>
							{campaignNo}
						</Tag>
					);
				}
				return "-";
			},
			filters: getUniqueFilters("Campaign_No_"),
			filterSearch: true,
			onFilter: (value, record) => record.Campaign_No_ === value,
		},
		{
			title: "Estado",
			key: "syncStatus",
			render: (_, record) => {
				// Mostrar estado especial si se está sincronizando
				if (record.syncing) {
					return <Tag color="processing">Sincronizando...</Tag>;
				}
				return getSyncStatusTag(record);
			},
			filters: getSyncStatusFilters(),
			filterSearch: true,
			onFilter: (value, record) => {
				switch (value) {
					case "draft":
						return record.isDraft;
					case "synced":
						return isInvoiceSynced(record) && !record.deleted;
					case "error":
						return !!record.syncError;
					case "pending":
						return record.pendingSync && !record.syncError && !record.deleted && !record.isDraft;
					case "deleted":
						return record.deleted;
					default:
						return true;
				}
			},
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => (
				<Space size="small">
					<Button
						icon={<EyeOutlined />}
						size="small"
						onClick={() => handleViewDetail(record)}
						title="Ver detalle"
					/>
					<Button
						icon={<EditOutlined />}
						size="small"
						onClick={() => handleEdit(record)}
						disabled={!record.isDraft}
						title={
							!record.isDraft
								? "Solo se pueden editar facturas en estado borrador"
								: "Editar factura"
						}
					/>
					<Button
						icon={<DeleteOutlined />}
						size="small"
						danger
						onClick={() => handleDelete(record)}
						disabled={
							record.No ||
							record.Pre_Assigned_No
						}
						title={
							!record.isDraft
								? "Solo se pueden eliminar facturas que no hayan sido sincronizadas, en estado 'borrador', 'pendiente' o 'error'"
								: record.deleted
									? "Esta factura ya ha sido eliminada"
									: "Eliminar factura"
						}
					/>
					<Button
						icon={<SyncOutlined />}
						size="small"
						type="primary"
						onClick={() => handleSync(record)}
						loading={record.syncing}
						disabled={isInvoiceSynced(record) || record.deleted || record.isDraft || record.syncing}
						title={
							record.syncing
								? "Sincronizando..."
								: ((!!record.Pre_Assigned_No || !!record.No) && isInvoiceSynced(record))
									? "La factura ya está sincronizada"
									: record.deleted
										? "Esta factura ha sido eliminada"
										: record.isDraft
											? "No se pueden sincronizar borradores"
											: "Sincronizar con Business Central"
						}
					/>
					{/* Solo mostrar el botón de crear nota de crédito en modo facturas */}
					{isInvoiceMode && showNotasCredito && (
						<Button
							icon={<RollbackOutlined />}
							size="small"
							type="dashed"
							onClick={() => handleCreateCreditMemo(record)}
							disabled={
								!record.No ||
								record.deleted ||
								record.Document_Type === "Credit Memo" ||
								record.pendingSync
							}
							title={
								!record.No
									? "Solo se pueden crear notas de crédito de facturas contabilizadas"
									: record.deleted
										? "No se pueden crear notas de crédito de facturas eliminadas"
										: record.Document_Type === "Credit Memo"
											? "No se pueden crear notas de crédito de notas de crédito"
											: "Crear nota de crédito"
							}
						/>
					)}
					{showTicketGenerico && (<Button
						icon={<PrinterOutlined />}
						size="small"
						onClick={() => handlePrintTicket(record)}
						title="Imprimir ticket"
					/>)}

					<DownloadTicketButtonSlot
						showButton={showTicket}
						invoice={record}
						setLoading={setLoading}
						disabled={record.pendingSync || !record.posted}
					/>

					<DownloadInvoiceButtonSlot
						showButton={showFactura}
						invoice={record}
						setLoading={setLoading}
						disabled={record.pendingSync || !record.posted}
					/>

					<PostInvoiceButtonSlot
						showButton={showContabilizar}
						invoice={record}
						clients={clients}
						setLoading={setLoading}
						country={country}
					/>
				</Space>
			),
		},
	];

	// Calcular totales de la factura seleccionada	
	const totals = useMemo(
		() => calculateInvoiceTotalsHelper(selectedInvoice, paymentLines, country),
		[selectedInvoice, paymentLines, country]
	);

	const {
		subtotal,
		vat,
		total,
		baseTotal,
		totalAjustado,
		ajuste,
		paymentTotal,
		pending,
		paymentMethodDiscount,
	} = totals;

	return (
		<div className="invoice-list-container">
			<div style={{ marginBottom: 16 }}>
				<h2 style={{ marginBottom: 16 }}>
					{isPendingMode
						? "Facturas pendientes de sincronizar"
						: isInvoiceMode
							? "Listado de facturas"
							: "Listado de abonos"}
				</h2>

				<Input
					placeholder="Buscar en todas las columnas..."
					prefix={<SearchOutlined />}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					style={{ width: 300 }}
					allowClear
				/>
			</div>

			<Table
				columns={columns}
				dataSource={filteredInvoices}
				rowKey="id"
				loading={loading}
				pagination={{
					...pagination,
					showSizeChanger: true,
					showQuickJumper: true,
					pageSizeOptions: ["5", "10", "20", "50"],
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} de ${total} elementos`,
				}}
				onChange={(newPagination) => setPagination(newPagination)}
				scroll={{ x: "max-content" }}
				size="small"
			/>

			{selectedInvoice && (
				<Drawer
					title={`Detalle de ${selectedInvoice.Document_Type === "Credit Memo"
						? "nota de crédito"
						: "factura"
						}: ${selectedInvoice.No ?? selectedInvoice.Pre_Assigned_No ?? "Sin número"}`}
					placement="right"
					onClose={() => setDetailVisible(false)}
					open={detailVisible}
					width={700}
					className="invoice-detail-drawer"
				>
					<div>
						<h3>Información general</h3>
						<p>
							<strong>Tipo:</strong>{" "}
							{getInvoiceTypeTag(selectedInvoice.Document_Type)}
						</p>
						<p>
							<strong>
								Nº{" "}
								{selectedInvoice.Document_Type === "Credit Memo"
									? "Nota de crédito"
									: "Factura"}
								:
							</strong>{" "}
							{selectedInvoice.No || "-"}
						</p>

						{selectedInvoice.Document_Type === "Credit Memo" &&
							selectedInvoice.Applies_to_Doc_No && (
								<p className="reference-invoice">
									<strong>Factura corregida:</strong>{" "}
									{selectedInvoice.Applies_to_Doc_No}
								</p>
							)}

						<p>
							<strong>Cliente:</strong> {selectedInvoice.Sell_to_Customer_No_}
						</p>
						<p>
							<strong>Nombre:</strong> {selectedInvoice.Bill_to_Name}
						</p>
						<p>
							<strong>Dirección:</strong> {selectedInvoice.Bill_to_Address}
						</p>
						<p>
							<strong>Envío a Colonia 2:</strong> {selectedInvoice.Direccion_2 || ""}
						</p>
						<p>
							<strong>Ciudad:</strong> {selectedInvoice.Bill_to_City}
						</p>
						<p>
							<strong>Número de cédula/RUC/Passaporte:</strong>{" "}
							{selectedInvoice.VAT_Registration_No_}
						</p>
						<p>
							<strong>País:</strong>{" "}
							{selectedInvoice.Bill_to_Country_Region_Code}
						</p>
						<p>
							<strong>Email:</strong> {selectedInvoice.E_Mail || ""}
						</p>
						<p>
							<strong>Teléfono:</strong> {selectedInvoice.Telefono || ""}
						</p>
						<p>
							<strong>Móvil:</strong> {selectedInvoice.Mobil || ""}
						</p>
						{colegio && (
							<p>
								<strong>Colegio:</strong> {colegio.No} - {colegio.nombre}
							</p>
						)}
						<p>
							<strong>Fecha:</strong>{" "}
							{
								selectedInvoice.Posting_Date
									? dayjs(selectedInvoice.Posting_Date).format("DD/MM/YYYY")
									: "-"
							}
						</p>
						<p>
							<strong>Caja:</strong> {selectedInvoice.caja || "-"}
						</p>
						<p>
							<strong>Turno:</strong> {selectedInvoice.turno || "-"}
						</p>
						{selectedInvoice.Cupon &&
							selectedInvoice.Tipo_Detalle &&
							selectedInvoice.No_Detalle && (
								<p>
									<strong>Cupón aplicado:</strong>{" "}
									<Tag color="green" icon={<TagOutlined />}>
										{selectedInvoice.Cupon}
									</Tag>
									<br />
									<small style={{ color: "#666" }}>
										<strong>Detalle:</strong> {selectedInvoice.Tipo_Detalle}:{" "}
										{selectedInvoice.No_Detalle}
									</small>
								</p>
							)}
						{selectedInvoice.Campaign_No_ && (
							<p>
								<strong>Campaña aplicada:</strong>{" "}
								<Tag color="blue" icon={<TagOutlined />}>
									{selectedInvoice.Campaign_No_}
								</Tag>
							</p>
						)}

						<h3 style={{ marginTop: "24px" }}>Líneas</h3>
						<Table
							columns={[
								{ title: "Nº", dataIndex: "No", key: "No" },
								{
									title: "Descripción",
									dataIndex: "Description",
									key: "Description",
								},
								{ title: "Cantidad", dataIndex: "Quantity", key: "Quantity" },
								{
									title: "Precio",
									dataIndex: "Unit_Price",
									key: "Unit_Price",
									render: (price) => `${formatNumberByCountry(price, country, true)}`,
								},
								{
									title: "IVA",
									dataIndex: "PorIva",
									key: "PorIva",
									render: (iva) => `${(iva || 0).toFixed(2)}%`,
								},
								{
									title: "Dto.",
									dataIndex: "Line_Discount_",
									key: "Line_Discount_",
									render: (discount, record) => {
										if (!discount) return "-";

										const clientDiscount = record.Client_Discount_ || 0;
										const couponDiscount = record.Coupon_Discount_ || 0;

										return (
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: "2px",
												}}
											>
												<span style={{ fontWeight: "bold" }}>{(discount || 0).toFixed(2)}%</span>
												{clientDiscount > 0 && (
													<Tag color="blue" size="small">
														Cliente: {clientDiscount}%
													</Tag>
												)}
												{couponDiscount > 0 && (
													<Tag color="green" size="small">
														Cupón: {couponDiscount}%
													</Tag>
												)}
											</div>
										);
									},
								},
								{
									title: "Importe",
									key: "Amount",
									render: (_, record) => {
										const quantity = record.Quantity || 0;
										const unitPrice = record.Unit_Price || 0;
										const discount = record.Line_Discount_ || 0;
										const discountAmount = unitPrice * (discount / 100);
										const finalPrice = unitPrice - discountAmount;
										return `${formatNumberByCountry((quantity * finalPrice), country, true)}`;
									},
								},
							]}
							dataSource={selectedInvoice.lines || []}
							pagination={false}
							scroll={{ x: "max-content" }}
						/>

						{selectedInvoice.lines && (
							<div className="invoice-totals" style={{ marginTop: "24px" }}>
								<div className="invoice-totals-item">
									<span>Subtotal:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{formatNumberByCountry(subtotal, country, true)}
									</span>
								</div>
								<div className="invoice-totals-item">
									<span>IVA:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{formatNumberByCountry(vat, country, true)}
									</span>
								</div>
								<div className="invoice-totals-item">
									<span>Total antes de descuentos:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{formatNumberByCountry(baseTotal, country, true)}
									</span>
								</div>

								{paymentMethodDiscount > 0 && (
									<div className="invoice-totals-item">
										<span>
											Descuento forma de pago ({paymentMethodDiscount}%):
										</span>
										<span className="discount-amount">
											{formatNumberByCountry(
												-paymentLines.reduce((sum, l) => sum + (l.Dto_Amount || 0), 0),
												country,
												true
											)}
										</span>
									</div>
								)}

								{paymentLines && paymentLines.length > 1 && (
									<div className="invoice-totals-item">
										<span>Descuento forma de pago:</span>
										<span style={{ color: "#ff4d4f" }}>
											No aplicable con múltiples formas de pago
										</span>
									</div>
								)}

								<div className="invoice-totals-item grand-total">
									<span>Total:</span>
									<span
										className={
											selectedInvoice.Document_Type === "Credit Memo"
												? "negative-amount"
												: ""
										}
									>
										{formatNumberByCountry(total || 0, country, true)}
									</span>
								</div>

								{selectedInvoice.Document_Type === "Credit Memo" && (
									<div className="credit-memo-note">
										Esta nota de crédito representa una devolución o corrección
										de la factura indicada.
									</div>
								)}
							</div>
						)}

						{/* Sección de pagos */}
						{paymentLines && paymentLines.length > 0 && (
							<>
								<h3 style={{ marginTop: "24px" }}>Pagos</h3>
								<Table
									columns={[
										{
											title: "Forma de pago",
											dataIndex: "Payment_Method",
											key: "Payment_Method",
											render: (text, record) => {
												// Mostrar texto especial para la fila de ajuste
												if (record.isAdjustment) {
													return (
														<span style={{ fontWeight: "bold" }}>
															Ajuste 4 %
														</span>
													);
												}

												return (
													<span>
														{text}
														{record.Dto > 0 && paymentMethodDiscount > 0 && (
															<Tag color="green" style={{ marginLeft: 8 }}>
																Dto: {record.Dto}%
															</Tag>
														)}
													</span>
												);
											},
										},
										{
											title: "Importe",
											dataIndex: "Amount",
											key: "Amount",
											render: (amount, record) => {
												const amountNoDiscount = parseFloat(amount ?? 0); //monto ya neto
												const netValue = amountNoDiscount;
												const displayValue = `${formatNumberByCountry(netValue || 0, country, true)} `;
												return record.isAdjustment ? (
													<span style={{ fontWeight: "bold", color: "#fa1414ff" }}>
														- {displayValue}
													</span>
												) : (
													displayValue
												);
											},
										},
									]}
									dataSource={paymentLines}
									pagination={false}
									scroll={{ x: "max-content" }}
								/>

								<div className="invoice-totals" style={{ marginTop: "24px" }}>
									<div className="invoice-totals-item grand-total">
										<span>Total pagado:</span>
										<span>{formatNumberByCountry((paymentTotal - ajuste), country, true)}</span>
									</div>
									<div className="invoice-totals-item grand-total">
										<span>Pendiente:</span>
										<span>
											{formatNumberByCountry((Math.abs(pending) - Math.abs(ajuste)), country, true)}
										</span>
									</div>
								</div>
							</>
						)}
					</div>
				</Drawer>
			)}

			{/* Modal de ticket para impresión */}
			{selectedInvoiceForTicket && (
				<TicketSectionSlot
					open={ticketModalVisible}
					onClose={() => {
						setTicketModalVisible(false);
						setSelectedClientForTicket(null);
						setSelectedTPVForTicket(null);
					}}
					invoice={selectedInvoiceForTicket}
					lines={selectedInvoiceForTicket.lines || []}
					caja={selectedInvoiceForTicket.caja || "1"}
					turno={selectedInvoiceForTicket.turno || "1"}
					paymentLines={selectedInvoiceForTicket.paymentLines || []}
					selectedClient={selectedClientForTicket}
					selectedTPV={selectedTPVForTicket}
					country={country}
				/>
			)}
		</div>
	);
};

export default InvoiceList;
